import { UserTravelProfile, TravelHistoryEntry } from './types/anthropic';
import type { PriceMonitoringPreferences, TrackedRoute } from './types/anthropic';

/**
 * User Profile Manager
 * Manages user travel profiles, history, and booking patterns
 * Uses localStorage for persistence
 */

const STORAGE_KEY = 'dial_a_flight_user_profile';
const PRICE_MONITORING_KEY = 'dial_a_flight_price_monitoring';

export class UserProfileManager {
  /**
   * Get user profile from localStorage
   */
  static getProfile(): UserTravelProfile | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  }

  /**
   * Save user profile to localStorage
   */
  static saveProfile(profile: UserTravelProfile): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Error saving user profile:', error);
    }
  }

  /**
   * Get price monitoring preferences
   */
  static getPriceMonitoringPreferences(): PriceMonitoringPreferences {
    if (typeof window === 'undefined') {
      return this.getDefaultPriceMonitoringPreferences();
    }
    
    try {
      const stored = localStorage.getItem(PRICE_MONITORING_KEY);
      if (!stored) return this.getDefaultPriceMonitoringPreferences();
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading price monitoring preferences:', error);
      return this.getDefaultPriceMonitoringPreferences();
    }
  }

  /**
   * Save price monitoring preferences
   */
  static savePriceMonitoringPreferences(preferences: PriceMonitoringPreferences): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(PRICE_MONITORING_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving price monitoring preferences:', error);
    }
  }

  /**
   * Get default price monitoring preferences
   */
  private static getDefaultPriceMonitoringPreferences(): PriceMonitoringPreferences {
    return {
      enabled: true,
      trackedRoutes: [],
      alertThresholds: {
        percentageDrops: [10, 20, 30],
      },
      checkFrequency: 'daily',
      notificationChannels: ['email', 'in-app'],
    };
  }

  /**
   * Add tracked route for price monitoring
   */
  static addTrackedRoute(route: Omit<TrackedRoute, 'id' | 'createdAt' | 'priceHistory' | 'status'>): TrackedRoute {
    const preferences = this.getPriceMonitoringPreferences();
    
    const newRoute: TrackedRoute = {
      ...route,
      id: `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      priceHistory: [],
      status: 'active',
    };
    
    preferences.trackedRoutes.push(newRoute);
    this.savePriceMonitoringPreferences(preferences);
    
    return newRoute;
  }

  /**
   * Update tracked route
   */
  static updateTrackedRoute(routeId: string, updates: Partial<TrackedRoute>): void {
    const preferences = this.getPriceMonitoringPreferences();
    const routeIndex = preferences.trackedRoutes.findIndex(r => r.id === routeId);
    
    if (routeIndex !== -1) {
      preferences.trackedRoutes[routeIndex] = {
        ...preferences.trackedRoutes[routeIndex],
        ...updates,
      };
      this.savePriceMonitoringPreferences(preferences);
    }
  }

  /**
   * Remove tracked route
   */
  static removeTrackedRoute(routeId: string): void {
    const preferences = this.getPriceMonitoringPreferences();
    preferences.trackedRoutes = preferences.trackedRoutes.filter(r => r.id !== routeId);
    this.savePriceMonitoringPreferences(preferences);
  }

  /**
   * Get all tracked routes
   */
  static getTrackedRoutes(): TrackedRoute[] {
    return this.getPriceMonitoringPreferences().trackedRoutes;
  }

  /**
   * Initialize default profile
   */
  static initializeProfile(): UserTravelProfile {
    const defaultProfile: UserTravelProfile = {
      preferences: {
        preferredAirlines: [],
        preferredCabinClass: [],
        budgetRange: {
          min: 0,
          max: 10000,
          currency: 'GBP',
        },
        travelPurpose: [],
        dietaryRestrictions: [],
        seatPreferences: [],
        specialNeeds: [],
      },
      travelHistory: [],
      bookingPatterns: {
        averageAdvanceBookingDays: 0,
        preferredTravelMonths: [],
        frequentDestinations: [],
        averageTripDuration: 0,
        preferredDepartureTime: '',
      },
    };
    
    this.saveProfile(defaultProfile);
    return defaultProfile;
  }

  /**
   * Add travel history entry
   */
  static addTravelHistory(entry: TravelHistoryEntry): void {
    const profile = this.getProfile() || this.initializeProfile();
    profile.travelHistory.push(entry);
    this.updateBookingPatterns(profile);
    this.saveProfile(profile);
  }

  /**
   * Update user preferences
   */
  static updatePreferences(preferences: Partial<UserTravelProfile['preferences']>): void {
    const profile = this.getProfile() || this.initializeProfile();
    profile.preferences = { ...profile.preferences, ...preferences };
    this.saveProfile(profile);
  }

  /**
   * Analyze and update booking patterns based on travel history
   */
  private static updateBookingPatterns(profile: UserTravelProfile): void {
    const history = profile.travelHistory;
    
    if (history.length === 0) return;

    // Calculate average advance booking days
    const advanceBookingDays = history
      .map(entry => {
        const bookingDate = new Date();
        const departureDate = new Date(entry.departureDate);
        return Math.floor((departureDate.getTime() - bookingDate.getTime()) / (1000 * 60 * 60 * 24));
      })
      .filter(days => days > 0);
    
    if (advanceBookingDays.length > 0) {
      profile.bookingPatterns.averageAdvanceBookingDays = 
        Math.floor(advanceBookingDays.reduce((a, b) => a + b, 0) / advanceBookingDays.length);
    }

    // Calculate frequent destinations
    const destinationCounts = history.reduce((acc, entry) => {
      acc[entry.destination] = (acc[entry.destination] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    profile.bookingPatterns.frequentDestinations = Object.entries(destinationCounts)
      .map(([destination, count]) => ({ destination, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate preferred travel months
    const monthCounts = history.reduce((acc, entry) => {
      const month = new Date(entry.departureDate).toLocaleString('default', { month: 'long' });
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    profile.bookingPatterns.preferredTravelMonths = Object.entries(monthCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([month]) => month);

    // Calculate average trip duration
    const durations = history
      .filter(entry => entry.returnDate)
      .map(entry => {
        const departure = new Date(entry.departureDate);
        const returnDate = new Date(entry.returnDate!);
        return Math.floor((returnDate.getTime() - departure.getTime()) / (1000 * 60 * 60 * 24));
      });

    if (durations.length > 0) {
      profile.bookingPatterns.averageTripDuration = 
        Math.floor(durations.reduce((a, b) => a + b, 0) / durations.length);
    }
  }

  /**
   * Get personalization insights for Claude prompts
   */
  static getPersonalizationContext(profile: UserTravelProfile): string {
    const { preferences, travelHistory, bookingPatterns } = profile;
    
    let context = 'User Travel Profile:\n';

    // Preferences
    if (preferences.preferredAirlines.length > 0) {
      context += `- Preferred Airlines: ${preferences.preferredAirlines.join(', ')}\n`;
    }
    if (preferences.preferredCabinClass.length > 0) {
      context += `- Preferred Cabin Class: ${preferences.preferredCabinClass.join(', ')}\n`;
    }
    if (preferences.budgetRange.max > 0) {
      context += `- Budget Range: ${preferences.budgetRange.currency} ${preferences.budgetRange.min} - ${preferences.budgetRange.max}\n`;
    }
    if (preferences.travelPurpose.length > 0) {
      context += `- Travel Purposes: ${preferences.travelPurpose.join(', ')}\n`;
    }

    // Booking Patterns
    if (bookingPatterns.averageAdvanceBookingDays > 0) {
      context += `- Typically books ${bookingPatterns.averageAdvanceBookingDays} days in advance\n`;
    }
    if (bookingPatterns.frequentDestinations.length > 0) {
      const topDestinations = bookingPatterns.frequentDestinations.slice(0, 3).map(d => d.destination).join(', ');
      context += `- Frequent Destinations: ${topDestinations}\n`;
    }
    if (bookingPatterns.preferredTravelMonths.length > 0) {
      context += `- Preferred Travel Months: ${bookingPatterns.preferredTravelMonths.join(', ')}\n`;
    }
    if (bookingPatterns.averageTripDuration > 0) {
      context += `- Average Trip Duration: ${bookingPatterns.averageTripDuration} days\n`;
    }

    // Recent Travel History
    if (travelHistory.length > 0) {
      const recentTrips = travelHistory.slice(-3).reverse();
      context += `\nRecent Travel History:\n`;
      recentTrips.forEach((trip, index) => {
        context += `${index + 1}. ${trip.destination} (${trip.airline}, ${trip.cabinClass})${trip.rating ? ` - Rated ${trip.rating}/5` : ''}\n`;
      });
    }

    return context;
  }

  /**
   * Import travel history from bookings
   */
  static importFromBookings(bookings: any[]): void {
    const profile = this.getProfile() || this.initializeProfile();
    
    bookings.forEach(booking => {
      const entry: TravelHistoryEntry = {
        id: booking.id,
        destination: booking.destination,
        departureDate: booking.departureDate,
        returnDate: booking.returnDate,
        airline: booking.airline,
        cabinClass: booking.cabinClass || 'economy',
        purpose: 'leisure',
      };
      
      // Check if entry already exists
      const exists = profile.travelHistory.some(h => h.id === entry.id);
      if (!exists) {
        profile.travelHistory.push(entry);
      }
    });

    this.updateBookingPatterns(profile);
    this.saveProfile(profile);
  }

  /**
   * Clear all profile data
   */
  static clearProfile(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(STORAGE_KEY);
  }
}
